import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, AlertTriangle, CheckCircle2, Play, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { Button } from "../../components/ui/button/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";

export interface BlockedJobsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: number;
  blockedCount: number;
  fingerprint: string;
  availableCredits: string | null;
  isUnlimited: boolean;
  onResume: (expectedCount: number, fingerprint: string) => Promise<{ success: true }>;
  onDiscard: (expectedCount: number, fingerprint: string) => Promise<{ success: true }>;
  onRefresh: () => Promise<void>;
}

export function BlockedJobsDialog({
  open,
  onOpenChange,
  blockedCount,
  fingerprint,
  availableCredits,
  isUnlimited,
  onResume,
  onDiscard,
  onRefresh,
}: BlockedJobsDialogProps): React.JSX.Element {
  const { t } = useTranslation();
  const notifications = useOptionalNotifications();

  const [busy, setBusy] = useState<"resume" | "discard" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hasEnoughCredits =
    isUnlimited || BigInt(availableCredits ?? "0") >= BigInt(blockedCount);

  async function handleResume(): Promise<void> {
    setBusy("resume");
    setErrorMessage(null);
    try {
      await onResume(blockedCount, fingerprint);
      notifications?.pushNotification({
        kind: "success",
        message: t("messageBalance.blockedJobs.toasts.resumedSuccess", { count: blockedCount }),
      });
      onOpenChange(false);
    } catch (err: any) {
      if (err?.code === "MESSAGE_BALANCE_BLOCKED_JOBS_CHANGED" || err?.status === 409) {
        setErrorMessage(t("messageBalance.blockedJobs.errors.jobsChanged"));
        await onRefresh();
      } else if (err?.code === "INSUFFICIENT_MESSAGE_CREDITS") {
        setErrorMessage(t("messageBalance.blockedJobs.errors.insufficientCredits"));
      } else {
        setErrorMessage(err?.message ?? t("messageBalance.blockedJobs.errors.resumeFailed"));
      }
    } finally {
      setBusy(null);
    }
  }

  async function handleDiscard(): Promise<void> {
    setBusy("discard");
    setErrorMessage(null);
    try {
      await onDiscard(blockedCount, fingerprint);
      notifications?.pushNotification({
        kind: "success",
        message: t("messageBalance.blockedJobs.toasts.discardedSuccess", { count: blockedCount }),
      });
      onOpenChange(false);
    } catch (err: any) {
      if (err?.code === "MESSAGE_BALANCE_BLOCKED_JOBS_CHANGED" || err?.status === 409) {
        setErrorMessage(t("messageBalance.blockedJobs.errors.jobsChanged"));
        await onRefresh();
      } else {
        setErrorMessage(err?.message ?? t("messageBalance.blockedJobs.errors.discardFailed"));
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(next) => !busy && onOpenChange(next)}>
      <AlertDialogContent
        className="w-[min(calc(100vw-2rem),30rem)] p-4 sm:p-6 max-h-[90dvh] overflow-y-auto"
        data-testid="blocked-jobs-dialog"
      >
        <AlertDialogTitle className="flex items-center gap-2 text-start">
          <AlertTriangle size={18} className="text-warning shrink-0" aria-hidden="true" />
          <span>{t("messageBalance.blockedJobs.title")}</span>
        </AlertDialogTitle>

        <AlertDialogDescription className="text-start break-words text-xs leading-relaxed text-muted">
          {t("messageBalance.blockedJobs.description")}
        </AlertDialogDescription>

        <div className="grid gap-3 py-2 min-w-0">
          <div className="grid grid-cols-2 gap-2 text-sm rounded-lg border border-line bg-surface-muted/50 p-3 min-w-0">
            <div className="min-w-0">
              <span className="text-xs text-muted block truncate">{t("messageBalance.blockedJobs.pausedJobs")}</span>
              <span className="text-base font-semibold text-foreground" data-testid="blocked-jobs-count">
                <LtrValue>{blockedCount}</LtrValue>
              </span>
            </div>
            <div className="min-w-0">
              <span className="text-xs text-muted block truncate">{t("messageBalance.blockedJobs.requiredCredits")}</span>
              <span className="text-base font-semibold text-foreground">
                <LtrValue>{blockedCount}</LtrValue>
              </span>
            </div>
            <div className="col-span-2 pt-2 border-t border-line/60 min-w-0">
              <span className="text-xs text-muted block truncate">{t("messageBalance.blockedJobs.currentAvailable")}</span>
              <span className="text-sm font-semibold text-foreground" data-testid="blocked-jobs-available">
                {isUnlimited ? (
                  <span className="text-success">{t("messageBalance.unlimited")}</span>
                ) : (
                  <LtrValue>{availableCredits ?? "0"}</LtrValue>
                )}
              </span>
            </div>
          </div>

          {!hasEnoughCredits ? (
            <div
              className="flex items-start gap-2.5 rounded-lg border border-warning/40 bg-warning-muted/40 p-3 text-xs text-warning min-w-0 text-start"
              role="alert"
            >
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <div className="min-w-0 break-words">
                <p className="m-0 font-medium">{t("messageBalance.blockedJobs.insufficientCreditsWarningTitle")}</p>
                <p className="m-0 mt-0.5 text-muted break-words leading-relaxed">
                  {t("messageBalance.blockedJobs.insufficientCreditsWarningDesc")}
                </p>
              </div>
            </div>
          ) : null}

          {errorMessage ? (
            <div
              className="flex items-center gap-2 rounded-lg bg-destructive/10 p-2.5 text-xs text-destructive min-w-0 text-start"
              role="alert"
            >
              <AlertCircle size={15} className="shrink-0" />
              <span className="break-words">{errorMessage}</span>
            </div>
          ) : null}
        </div>

        <AlertDialogActions className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2.5 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full sm:w-auto"
            onClick={() => onOpenChange(false)}
            disabled={busy !== null}
          >
            {t("common.close")}
          </Button>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="border-destructive/30 text-destructive hover:bg-destructive/10 w-full sm:w-auto"
              onClick={() => void handleDiscard()}
              disabled={busy !== null || blockedCount === 0}
              data-testid="blocked-jobs-discard-btn"
            >
              <Trash2 size={14} className="me-1.5 shrink-0" aria-hidden="true" />
              <span>{busy === "discard" ? t("common.saving") : t("messageBalance.blockedJobs.discardAll")}</span>
            </Button>

            <Button
              type="button"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => void handleResume()}
              disabled={busy !== null || !hasEnoughCredits || blockedCount === 0}
              data-testid="blocked-jobs-resume-btn"
            >
              <Play size={14} className="me-1.5 shrink-0" aria-hidden="true" />
              <span>{busy === "resume" ? t("common.saving") : t("messageBalance.blockedJobs.resumeAll")}</span>
            </Button>
          </div>
        </AlertDialogActions>
      </AlertDialogContent>
    </AlertDialog>
  );
}
